/**
 * Local record of tours this user has finished or skipped, per user and organization.
 *
 * Two jobs the server alone cannot do:
 *
 * - **Immediate.** The tour is marked done before the PUT resolves, so a reload or a second tab
 *   straight after Finish does not show it again while the request is still in flight.
 * - **Durable.** A PUT that fails is kept as a pending write and retried the next time the provider
 *   loads for the same user. The storage key carries the user id, and each pending write is stamped
 *   with it too, so a shared browser never sends one coach's progress under another's token.
 *
 * Every storage access is wrapped: private windows, a full quota, or blocked site data must degrade
 * to "no local guard", never to a broken page.
 *
 * **Re-show.** An admin can re-show a tour to everyone, which the API reports as the tour's
 * `resetAt`. A mark or a pending write from before that moment would otherwise keep the tour hidden
 * on this browser for good, so each mark carries when it was made, and anything older than the
 * tour's `resetAt` is ignored and dropped. A mark stored by an older build has no time: it counts as
 * older than any reset, and stays valid for a tour that was never reset.
 */
import type { RecordTourProgressRequest, Tour, TourProgress } from "../../types/tours";

export interface PendingTourWrite {
  userId: string;
  tourKey: string;
  body: RecordTourProgressRequest;
}

interface GuardState {
  /** Tour key to the step keys marked done on this device. */
  done: Record<string, string[]>;
  /**
   * Tour key to when it was last marked done (ISO-8601). A parallel map rather than a new shape for
   * `done`, so a tab still running an older build reads the same file without dropping entries.
   */
  doneAt: Record<string, string>;
  pending: PendingTourWrite[];
}

/** What the guard needs from a tour to honour a re-show. */
export type TourResetInfo = Pick<Tour, "key" | "resetAt">;

export const tourGuardStorageKey = (userId: string, orgId: string): string =>
  `rp.tours.${userId}.${orgId}`;

const emptyState = (): GuardState => ({ done: {}, doneAt: {}, pending: [] });

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((item) => typeof item === "string");

const parseState = (raw: string | null): GuardState => {
  if (!raw) return emptyState();
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return emptyState();
    const { done, doneAt, pending } = parsed as {
      done?: unknown;
      doneAt?: unknown;
      pending?: unknown;
    };
    const state = emptyState();
    if (typeof done === "object" && done !== null && !Array.isArray(done)) {
      for (const [tourKey, keys] of Object.entries(done)) {
        if (isStringArray(keys)) state.done[tourKey] = keys;
      }
    }
    if (typeof doneAt === "object" && doneAt !== null && !Array.isArray(doneAt)) {
      for (const [tourKey, markedAt] of Object.entries(doneAt)) {
        if (typeof markedAt === "string" && tourKey in state.done) state.doneAt[tourKey] = markedAt;
      }
    }
    if (Array.isArray(pending)) {
      state.pending = pending.filter(
        (write): write is PendingTourWrite =>
          typeof write === "object" &&
          write !== null &&
          typeof (write as PendingTourWrite).userId === "string" &&
          typeof (write as PendingTourWrite).tourKey === "string" &&
          typeof (write as PendingTourWrite).body === "object" &&
          (write as PendingTourWrite).body !== null
      );
    }
    return state;
  } catch {
    return emptyState();
  }
};

const timeOf = (iso: string | null | undefined): number | null => {
  if (!iso) return null;
  const time = Date.parse(iso);
  return Number.isNaN(time) ? null : time;
};

/** True when something recorded at `recordedAt` predates the reset; an untimed record always does. */
const predates = (recordedAt: string | null | undefined, resetTime: number): boolean => {
  const time = timeOf(recordedAt);
  return time === null || time < resetTime;
};

/**
 * Drops the marks and pending writes a re-show has made stale. Mutates `state`; returns whether it
 * changed anything, so a caller writes back only when there is something to write.
 */
const dropStale = (state: GuardState, tours: readonly TourResetInfo[]): boolean => {
  const resets = new Map<string, number>();
  for (const tour of tours) {
    const time = timeOf(tour.resetAt);
    if (time !== null) resets.set(tour.key, time);
  }
  if (resets.size === 0) return false;

  let changed = false;
  for (const tourKey of Object.keys(state.done)) {
    const resetTime = resets.get(tourKey);
    if (resetTime !== undefined && predates(state.doneAt[tourKey], resetTime)) {
      delete state.done[tourKey];
      delete state.doneAt[tourKey];
      changed = true;
    }
  }
  const pending = state.pending.filter((write) => {
    const resetTime = resets.get(write.tourKey);
    return resetTime === undefined || !predates(write.body.seenAt, resetTime);
  });
  if (pending.length !== state.pending.length) {
    state.pending = pending;
    changed = true;
  }
  return changed;
};

const safeStorage = (): Storage | null => {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    return null;
  }
};

export interface TourProgressGuard {
  /**
   * Marks the tour's steps done locally, straight away. `markedAt` defaults to now; pass the run's
   * `seenAt` so the mark and its pending write carry the same time.
   */
  markDone: (tourKey: string, stepKeys: readonly string[], markedAt?: string) => void;
  /**
   * Server progress with this device's done keys merged in. A mark older than its tour's `resetAt`
   * in `tours` is left out, and removed from storage.
   */
  mergeInto: (progress: readonly TourProgress[], tours?: readonly TourResetInfo[]) => TourProgress[];
  addPending: (tourKey: string, body: RecordTourProgressRequest) => void;
  removePending: (tourKey: string, seenAt: string) => void;
  /**
   * Pending writes for this guard's user only. A write whose `seenAt` is older than its tour's
   * `resetAt` in `tours` is left out, and removed from storage, so it can never re-hide the tour.
   */
  pendingWrites: (tours?: readonly TourResetInfo[]) => PendingTourWrite[];
  /**
   * Calls `onDone` with each tour key another tab marked done. Returns an unsubscribe function.
   */
  subscribe: (onDone: (tourKey: string) => void) => () => void;
}

export const createTourProgressGuard = (
  userId: string,
  orgId: string,
  storage: Storage | null = safeStorage()
): TourProgressGuard => {
  const key = tourGuardStorageKey(userId, orgId);

  const read = (): GuardState => {
    try {
      return parseState(storage?.getItem(key) ?? null);
    } catch {
      return emptyState();
    }
  };

  const write = (state: GuardState) => {
    try {
      storage?.setItem(key, JSON.stringify(state));
    } catch (error) {
      console.warn("Could not save tour progress locally:", error);
    }
  };

  /** Current state with stale records dropped; written back only when something was dropped. */
  const readCurrent = (tours: readonly TourResetInfo[]): GuardState => {
    const state = read();
    if (dropStale(state, tours)) write(state);
    return state;
  };

  return {
    markDone: (tourKey, stepKeys, markedAt = new Date().toISOString()) => {
      const state = read();
      state.done[tourKey] = Array.from(new Set([...(state.done[tourKey] ?? []), ...stepKeys]));
      state.doneAt[tourKey] = markedAt;
      write(state);
    },

    // Filtered in memory as well as in storage: a store that reads but cannot write (a full quota)
    // must still stop hiding a re-shown tour.
    mergeInto: (progress, tours = []) => {
      const { done } = readCurrent(tours);
      const merged = progress.map((entry) =>
        done[entry.tourKey]
          ? {
              ...entry,
              seenStepKeys: Array.from(new Set([...entry.seenStepKeys, ...done[entry.tourKey]])),
            }
          : entry
      );
      for (const [tourKey, keys] of Object.entries(done)) {
        if (!merged.some((entry) => entry.tourKey === tourKey)) {
          merged.push({ tourKey, outcome: null, seenStepKeys: [...keys] });
        }
      }
      return merged;
    },

    addPending: (tourKey, body) => {
      const state = read();
      // One pending write per tour: the server merges by union, so the latest run's keys plus the
      // local done set already cover anything an older pending write carried.
      state.pending = [
        ...state.pending.filter((entry) => entry.tourKey !== tourKey),
        { userId, tourKey, body },
      ];
      write(state);
    },

    removePending: (tourKey, seenAt) => {
      const state = read();
      // Matched on seenAt too, so a slow success for an older run does not delete a newer one.
      state.pending = state.pending.filter(
        (entry) => !(entry.tourKey === tourKey && entry.body.seenAt === seenAt)
      );
      write(state);
    },

    pendingWrites: (tours = []) =>
      readCurrent(tours).pending.filter((entry) => entry.userId === userId),

    subscribe: (onDone) => {
      if (typeof window === "undefined") return () => {};
      const handleStorage = (event: StorageEvent) => {
        if (event.key !== key) return;
        const before = parseState(event.oldValue).done;
        const after = parseState(event.newValue).done;
        for (const [tourKey, keys] of Object.entries(after)) {
          const previous = new Set(before[tourKey] ?? []);
          if (keys.some((stepKey) => !previous.has(stepKey))) onDone(tourKey);
        }
      };
      window.addEventListener("storage", handleStorage);
      return () => window.removeEventListener("storage", handleStorage);
    },
  };
};
